import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import * as Icons from 'lucide-react';
import {
  Plus, Save, ArrowLeft, Trash2, Copy, ArrowUp, ArrowDown, Loader2,
  MousePointer2, Type,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import IconPicker from '../components/IconPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CANVAS_W = 1200;
const CANVAS_H = 600;

const iconFor = (name) => {
  if (!name) return Icons.Sparkles;
  const key = String(name)
    .split(/[-_\s]+/).filter(Boolean)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join('');
  return Icons[key] || Icons.Sparkles;
};

const newBox = (variant = 'card') => ({
  id: `box_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
  x: 60, y: 60, w: 260, h: variant === 'text' ? 90 : 140, z: 1, rotation: 0,
  variant,
  text_align: variant === 'text' ? 'center' : 'left',
  icon: variant === 'text' ? '' : 'sparkles',
  title: variant === 'text' ? 'Bold headline' : 'New panel',
  description: variant === 'text' ? '' : 'Click to edit this panel.',
  bg_color: variant === 'text' ? 'transparent' : '#ffffff',
  text_color: '#0a1a2f',
  accent_color: '#d4af37',
  border_radius: variant === 'text' ? 0 : 12,
  shadow: variant !== 'text',
  show_icon: variant !== 'text',
  title_size: variant === 'text' ? 32 : 16,
});

const snapTo = (v, snap) => (snap ? Math.round(v / 20) * 20 : v);

// ─── Magnetic snap-line helpers ──────────────────────────────────────
const SNAP_THRESHOLD = 6;

function computeMagneticSnap(movingId, rawX, rawY, w, h, allBoxes) {
  const others = allBoxes.filter(b => b.id !== movingId);
  // Collect guide positions from other boxes + canvas edges + canvas center
  const vGuides = [0, CANVAS_W / 2, CANVAS_W];
  const hGuides = [0, CANVAS_H / 2, CANVAS_H];
  for (const ob of others) {
    vGuides.push(ob.x, ob.x + ob.w / 2, ob.x + ob.w);
    hGuides.push(ob.y, ob.y + ob.h / 2, ob.y + ob.h);
  }
  const movingEdgesX = [rawX, rawX + w / 2, rawX + w];
  const movingEdgesY = [rawY, rawY + h / 2, rawY + h];
  let snappedX = rawX, snappedY = rawY;
  const guides = [];
  // Closest vertical
  let bestDx = SNAP_THRESHOLD + 1, bestVPos = null, bestVEdge = -1;
  for (const gx of vGuides) {
    for (let i = 0; i < 3; i++) {
      const d = Math.abs(movingEdgesX[i] - gx);
      if (d < bestDx) { bestDx = d; bestVPos = gx; bestVEdge = i; }
    }
  }
  if (bestDx <= SNAP_THRESHOLD && bestVPos !== null) {
    snappedX = bestVEdge === 0 ? bestVPos : bestVEdge === 1 ? bestVPos - w / 2 : bestVPos - w;
    guides.push({ type: 'v', pos: bestVPos });
  }
  // Closest horizontal
  let bestDy = SNAP_THRESHOLD + 1, bestHPos = null, bestHEdge = -1;
  for (const gy of hGuides) {
    for (let i = 0; i < 3; i++) {
      const d = Math.abs(movingEdgesY[i] - gy);
      if (d < bestDy) { bestDy = d; bestHPos = gy; bestHEdge = i; }
    }
  }
  if (bestDy <= SNAP_THRESHOLD && bestHPos !== null) {
    snappedY = bestHEdge === 0 ? bestHPos : bestHEdge === 1 ? bestHPos - h / 2 : bestHPos - h;
    guides.push({ type: 'h', pos: bestHPos });
  }
  snappedX = Math.max(0, Math.min(CANVAS_W - w, snappedX));
  snappedY = Math.max(0, Math.min(CANVAS_H - h, snappedY));
  return { x: snappedX, y: snappedY, guides };
}

function computeResizeSnap(movingId, x, y, rawW, rawH, allBoxes) {
  const others = allBoxes.filter(b => b.id !== movingId);
  const vGuides = [CANVAS_W / 2, CANVAS_W];
  const hGuides = [CANVAS_H / 2, CANVAS_H];
  for (const ob of others) {
    vGuides.push(ob.x, ob.x + ob.w / 2, ob.x + ob.w);
    hGuides.push(ob.y, ob.y + ob.h / 2, ob.y + ob.h);
  }
  let snappedW = rawW, snappedH = rawH;
  const guides = [];
  const rightEdge = x + rawW;
  const bottomEdge = y + rawH;
  // Snap right edge
  let bestDx = SNAP_THRESHOLD + 1, bestVPos = null;
  for (const gx of vGuides) {
    const d = Math.abs(rightEdge - gx);
    if (d < bestDx) { bestDx = d; bestVPos = gx; }
  }
  if (bestDx <= SNAP_THRESHOLD && bestVPos !== null) {
    snappedW = bestVPos - x;
    guides.push({ type: 'v', pos: bestVPos });
  }
  // Snap bottom edge
  let bestDy = SNAP_THRESHOLD + 1, bestHPos = null;
  for (const gy of hGuides) {
    const d = Math.abs(bottomEdge - gy);
    if (d < bestDy) { bestDy = d; bestHPos = gy; }
  }
  if (bestDy <= SNAP_THRESHOLD && bestHPos !== null) {
    snappedH = bestHPos - y;
    guides.push({ type: 'h', pos: bestHPos });
  }
  snappedW = Math.max(80, Math.min(CANVAS_W - x, snappedW));
  snappedH = Math.max(40, Math.min(CANVAS_H - y, snappedH));
  return { w: snappedW, h: snappedH, guides };
}

// One canvas box — supports drag body + resize handle at bottom-right.
const CanvasBox = ({ box, selected, onSelect, onChange, canvasScale, snap, getSnap, onGuidesChange }) => {
  const dragRef = useRef(null);

  const onPointerDown = useCallback((e, mode) => {
    e.stopPropagation();
    onSelect(box.id);
    const startX = e.clientX, startY = e.clientY;
    const startBox = { ...box };
    const move = (ev) => {
      const dx = (ev.clientX - startX) / canvasScale;
      const dy = (ev.clientY - startY) / canvasScale;
      if (mode === 'move') {
        let rawX = Math.max(0, Math.min(CANVAS_W - startBox.w, snapTo(startBox.x + dx, snap)));
        let rawY = Math.max(0, Math.min(CANVAS_H - startBox.h, snapTo(startBox.y + dy, snap)));
        if (getSnap) {
          const s = getSnap(box.id, rawX, rawY, startBox.w, startBox.h, 'move');
          rawX = s.x; rawY = s.y;
          if (onGuidesChange) onGuidesChange(s.guides);
        }
        onChange(box.id, { x: rawX, y: rawY });
      } else if (mode === 'resize') {
        let rawW = Math.max(80, Math.min(CANVAS_W - startBox.x, snapTo(startBox.w + dx, snap)));
        let rawH = Math.max(40, Math.min(CANVAS_H - startBox.y, snapTo(startBox.h + dy, snap)));
        if (getSnap) {
          const s = getSnap(box.id, startBox.x, startBox.y, rawW, rawH, 'resize');
          rawW = s.w; rawH = s.h;
          if (onGuidesChange) onGuidesChange(s.guides);
        }
        onChange(box.id, { w: rawW, h: rawH });
      }
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (onGuidesChange) onGuidesChange([]);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [box, onSelect, onChange, canvasScale, snap, getSnap, onGuidesChange]);

  const Icon = iconFor(box.icon);
  const isText = box.variant === 'text';
  const align = box.text_align || 'left';
  const alignClass = align === 'center' ? 'items-center text-center' : align === 'right' ? 'items-end text-right' : 'items-start text-left';
  return (
    <div
      ref={dragRef}
      onPointerDown={(e) => onPointerDown(e, 'move')}
      onClick={(e) => e.stopPropagation()}
      data-testid={`canvas-box-${box.id}`}
      style={{
        position: 'absolute',
        left: box.x,
        top: box.y,
        width: box.w,
        height: box.h,
        transform: `rotate(${box.rotation || 0}deg)`,
        backgroundColor: isText ? 'transparent' : box.bg_color,
        color: box.text_color,
        borderRadius: isText ? 0 : box.border_radius,
        boxShadow: !isText && box.shadow ? '0 12px 30px -8px rgba(10,26,47,0.35)' : 'none',
        outline: selected ? `2px solid #00457f` : isText ? `1px dashed ${box.text_color}30` : `1px solid ${box.text_color}15`,
        cursor: 'move',
        zIndex: box.z || 1,
        padding: isText ? 8 : 16,
        overflow: 'hidden',
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      <div className={`h-full flex flex-col gap-2 justify-center ${alignClass}`}>
        {!isText && box.show_icon && (
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${box.accent_color}22`, color: box.accent_color }}
          >
            <Icon className="w-5 h-5" strokeWidth={2.25} />
          </div>
        )}
        {box.title && (
          <div
            className="font-semibold leading-tight"
            style={{ fontSize: isText ? (box.title_size || 32) : 15 }}
          >
            {box.title}
          </div>
        )}
        {box.description && (
          <div
            className={`opacity-80 leading-snug ${isText ? '' : 'line-clamp-3 text-xs'}`}
            style={{ fontSize: isText ? 14 : undefined }}
          >
            {box.description}
          </div>
        )}
      </div>
      {selected && (
        <div
          onPointerDown={(e) => onPointerDown(e, 'resize')}
          className="absolute bottom-0 right-0 w-4 h-4 bg-[#00457f] cursor-se-resize rounded-tl"
          style={{ touchAction: 'none' }}
          data-testid={`canvas-resize-${box.id}`}
        />
      )}
    </div>
  );
};

const Inspector = ({ box, onChange, onDuplicate, onDelete, onSendBack, onBringForward }) => {
  if (!box) {
    return (
      <div className="p-4 text-sm text-slate-500">
        <MousePointer2 className="w-8 h-8 mx-auto mb-3 text-slate-300" />
        <p className="text-center">Select a panel to edit — or click <b>Add Panel</b> to drop a new one.</p>
      </div>
    );
  }
  const set = (patch) => onChange(box.id, patch);
  return (
    <div className="p-4 space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-700">
          {box.variant === 'text' ? 'Text block' : 'Panel'} {box.id.slice(-4)}
        </h4>
        <div className="flex gap-1">
          <button title="Send back" onClick={onSendBack} className="p-1 rounded hover:bg-slate-100" data-testid="inspector-send-back">
            <ArrowDown className="w-4 h-4" />
          </button>
          <button title="Bring forward" onClick={onBringForward} className="p-1 rounded hover:bg-slate-100" data-testid="inspector-bring-forward">
            <ArrowUp className="w-4 h-4" />
          </button>
          <button title="Duplicate" onClick={onDuplicate} className="p-1 rounded hover:bg-slate-100" data-testid="inspector-duplicate">
            <Copy className="w-4 h-4" />
          </button>
          <button title="Delete" onClick={onDelete} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid="inspector-delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Variant toggle */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
        {[
          { key: 'card', label: 'Card' },
          { key: 'text', label: 'Text only' },
        ].map((v) => (
          <button
            key={v.key}
            onClick={() => set({ variant: v.key })}
            className={`flex-1 text-xs font-medium py-1.5 rounded ${
              (box.variant || 'card') === v.key ? 'bg-white shadow text-[#00457f]' : 'text-slate-500'
            }`}
            data-testid={`inspector-variant-${v.key}`}
          >
            {v.label}
          </button>
        ))}
      </div>
      {box.variant !== 'text' && (
        <div>
          <label className="text-xs text-slate-500 block mb-1">Icon</label>
          <IconPicker value={box.icon} onChange={(name) => set({ icon: name })} testId="inspector-icon" />
          <label className="flex items-center gap-1 mt-1 text-xs text-slate-600">
            <input type="checkbox" checked={box.show_icon !== false} onChange={(e) => set({ show_icon: e.target.checked })} />
            Show icon
          </label>
        </div>
      )}
      <div>
        <label className="text-xs text-slate-500 block mb-1">
          {box.variant === 'text' ? 'Headline' : 'Title'}
        </label>
        <Input value={box.title} onChange={(e) => set({ title: e.target.value })} className="text-sm" />
      </div>
      <div>
        <label className="text-xs text-slate-500 block mb-1">
          {box.variant === 'text' ? 'Sub-line (optional)' : 'Description'}
        </label>
        <textarea
          value={box.description}
          onChange={(e) => set({ description: e.target.value })}
          rows={box.variant === 'text' ? 2 : 3}
          className="w-full text-sm px-3 py-2 border rounded-md"
        />
      </div>
      {box.variant === 'text' && (
        <>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Headline size: {box.title_size || 32}px</label>
            <input type="range" min="14" max="80" value={box.title_size || 32} onChange={(e) => set({ title_size: parseInt(e.target.value) })} className="w-full" />
          </div>
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
            {['left','center','right'].map(a => (
              <button
                key={a}
                onClick={() => set({ text_align: a })}
                className={`flex-1 text-xs py-1.5 rounded capitalize ${(box.text_align || 'left') === a ? 'bg-white shadow text-[#00457f]' : 'text-slate-500'}`}
                data-testid={`inspector-align-${a}`}
              >
                {a}
              </button>
            ))}
          </div>
        </>
      )}
      <div className="grid grid-cols-2 gap-2">
        {box.variant !== 'text' && (
          <div>
            <label className="text-xs text-slate-500 block mb-1">BG</label>
            <input type="color" value={box.bg_color} onChange={(e) => set({ bg_color: e.target.value })} className="w-full h-9 rounded border" />
          </div>
        )}
        <div>
          <label className="text-xs text-slate-500 block mb-1">Text</label>
          <input type="color" value={box.text_color} onChange={(e) => set({ text_color: e.target.value })} className="w-full h-9 rounded border" />
        </div>
        {box.variant !== 'text' && (
          <>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Accent</label>
              <input type="color" value={box.accent_color} onChange={(e) => set({ accent_color: e.target.value })} className="w-full h-9 rounded border" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Radius: {box.border_radius}</label>
              <input type="range" min="0" max="40" value={box.border_radius} onChange={(e) => set({ border_radius: parseInt(e.target.value) })} className="w-full" />
            </div>
          </>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Rotation: {box.rotation || 0}°</label>
          <input type="range" min="-15" max="15" value={box.rotation || 0} onChange={(e) => set({ rotation: parseInt(e.target.value) })} className="w-full" />
        </div>
        {box.variant !== 'text' && (
          <div className="flex items-end">
            <label className="flex items-center gap-1 text-xs text-slate-600">
              <input type="checkbox" checked={box.shadow !== false} onChange={(e) => set({ shadow: e.target.checked })} />
              Drop shadow
            </label>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 pt-2 border-t">
        <div>
          <label className="text-xs text-slate-500 block mb-1">X</label>
          <Input type="number" value={Math.round(box.x)} onChange={(e) => set({ x: parseFloat(e.target.value) || 0 })} className="text-sm" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Y</label>
          <Input type="number" value={Math.round(box.y)} onChange={(e) => set({ y: parseFloat(e.target.value) || 0 })} className="text-sm" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">W</label>
          <Input type="number" value={Math.round(box.w)} onChange={(e) => set({ w: parseFloat(e.target.value) || 80 })} className="text-sm" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">H</label>
          <Input type="number" value={Math.round(box.h)} onChange={(e) => set({ h: parseFloat(e.target.value) || 60 })} className="text-sm" />
        </div>
      </div>
      <p className="text-[11px] text-slate-400 pt-2 border-t">
        Tip: use ↑ ↓ ← → to nudge 1px, Shift+arrows to nudge 20px.
      </p>
    </div>
  );
};

const DEFAULT_LAYOUT = {
  name: 'Untitled Layout',
  description: '',
  eyebrow: '',
  section_title: '',
  section_subtitle: '',
  accent_color: '#d4af37',
  background: { type: 'none' },
  canvas_width: CANVAS_W,
  canvas_height: CANVAS_H,
  boxes: [],
};

const AdminLayoutBuilder = ({
  embedded = false,
  initialLayout = null,
  onSaveEmbedded = null,
  onCloseEmbedded = null,
  embeddedSaveLabel = 'Save Layout',
  embeddedTitle = null,
}) => {
  const navigate = useNavigate();
  const { layoutId: routeLayoutId } = useParams();
  const layoutId = embedded ? null : routeLayoutId;
  const isNew = embedded ? false : (!layoutId || layoutId === 'new');

  const [layout, setLayout] = useState(() =>
    embedded && initialLayout
      ? { ...DEFAULT_LAYOUT, ...initialLayout, boxes: initialLayout.boxes || [] }
      : { ...DEFAULT_LAYOUT }
  );
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [existingList, setExistingList] = useState([]);
  const [snap, setSnap] = useState(false);
  const [activeGuides, setActiveGuides] = useState([]);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const canvasWrapRef = useRef(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const boxesRef = useRef(layout.boxes);
  boxesRef.current = layout.boxes;

  const getSnap = useCallback((boxId, rawX, rawY, w, h, mode) => {
    if (mode === 'resize') return computeResizeSnap(boxId, rawX, rawY, w, h, boxesRef.current);
    return computeMagneticSnap(boxId, rawX, rawY, w, h, boxesRef.current);
  }, []);

  // Fetch existing layouts to show a sidebar list
  useEffect(() => {
    if (embedded) return;
    fetch(`${API_URL}/api/custom-layouts`).then(r => r.json()).then(setExistingList).catch(() => {});
  }, [saving, embedded]);

  // Load existing layout if editing
  useEffect(() => {
    if (embedded || isNew) return;
    fetch(`${API_URL}/api/custom-layouts/${layoutId}`)
      .then(r => r.json())
      .then(d => { if (d && d.id) setLayout(d); })
      .catch(() => toast.error('Failed to load layout'));
  }, [layoutId, isNew, embedded]);

  // Responsive canvas scaling
  useEffect(() => {
    const update = () => {
      if (canvasWrapRef.current) {
        const w = canvasWrapRef.current.clientWidth;
        setCanvasScale(Math.min(1, w / CANVAS_W));
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const selectedBox = layout.boxes.find(b => b.id === selectedId) || null;

  const updateBox = (id, patch) => {
    setLayout(prev => ({
      ...prev,
      boxes: prev.boxes.map(b => (b.id === id ? { ...b, ...patch } : b)),
    }));
  };

  const addBox = (variant = 'card') => {
    const b = newBox(variant);
    b.z = (Math.max(0, ...layout.boxes.map(x => x.z || 0)) + 1);
    setLayout(prev => ({ ...prev, boxes: [...prev.boxes, b] }));
    setSelectedId(b.id);
  };

  const duplicateBox = () => {
    if (!selectedBox) return;
    const b = { ...selectedBox, id: `box_${Date.now()}`, x: selectedBox.x + 20, y: selectedBox.y + 20 };
    setLayout(prev => ({ ...prev, boxes: [...prev.boxes, b] }));
    setSelectedId(b.id);
  };

  const deleteBox = () => {
    if (!selectedBox) return;
    setLayout(prev => ({ ...prev, boxes: prev.boxes.filter(b => b.id !== selectedBox.id) }));
    setSelectedId(null);
  };

  const bumpZ = (delta) => {
    if (!selectedBox) return;
    updateBox(selectedBox.id, { z: Math.max(1, (selectedBox.z || 1) + delta) });
  };

  // Arrow-key nudging for the selected box (1px, Shift = 20px). Skip when typing in an input.
  useEffect(() => {
    const isTyping = (t) => {
      if (!t) return false;
      const tag = t.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || t.isContentEditable;
    };
    const onKey = (e) => {
      if (!selectedId) return;
      if (isTyping(e.target)) return;
      const step = e.shiftKey ? 20 : 1;
      let dx = 0, dy = 0;
      if (e.key === 'ArrowLeft') dx = -step;
      else if (e.key === 'ArrowRight') dx = step;
      else if (e.key === 'ArrowUp') dy = -step;
      else if (e.key === 'ArrowDown') dy = step;
      else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteBox();
        return;
      } else return;
      e.preventDefault();
      setLayout(prev => ({
        ...prev,
        boxes: prev.boxes.map(b =>
          b.id === selectedId
            ? {
                ...b,
                x: Math.max(0, Math.min(CANVAS_W - b.w, b.x + dx)),
                y: Math.max(0, Math.min(CANVAS_H - b.h, b.y + dy)),
              }
            : b
        ),
      }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId]);

  const save = async () => {
    if (embedded) {
      if (onSaveEmbedded) {
        setSaving(true);
        try {
          await onSaveEmbedded(layout);
        } finally {
          setSaving(false);
        }
      }
      return;
    }
    if (!layout.name.trim()) { toast.error('Give the layout a name first.'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = isNew
        ? `${API_URL}/api/custom-layouts`
        : `${API_URL}/api/custom-layouts/${layoutId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(layout),
      });
      if (!res.ok) throw new Error(await res.text());
      const saved = await res.json();
      toast.success(isNew ? 'Layout saved to templates' : 'Layout updated');
      if (isNew) navigate(`/admin/layout-builder/${saved.id}`);
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const renameTemplate = async (id) => {
    if (!renameValue.trim()) { setRenamingId(null); return; }
    const token = localStorage.getItem('admin_token');
    try {
      const existing = existingList.find(l => l.id === id);
      await fetch(`${API_URL}/api/custom-layouts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...existing, name: renameValue.trim() }),
      });
      setExistingList(prev => prev.map(l => l.id === id ? { ...l, name: renameValue.trim() } : l));
      if (layoutId === id) setLayout(prev => ({ ...prev, name: renameValue.trim() }));
      toast.success('Template renamed');
    } catch { toast.error('Rename failed'); }
    setRenamingId(null);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/custom-layouts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setExistingList(prev => prev.filter(l => l.id !== id));
      toast.success('Template deleted');
      if (layoutId === id) navigate('/admin/layout-builder/new');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className={embedded ? 'bg-slate-100' : 'min-h-screen bg-slate-100'} data-testid="admin-layout-builder">
      {/* Top bar */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => (embedded ? onCloseEmbedded && onCloseEmbedded() : navigate('/admin/homepage'))}
            data-testid="builder-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> {embedded ? 'Close' : 'Back'}
          </Button>
          <div className="flex-1 min-w-[200px]">
            {embedded ? (
              <div className="font-semibold text-slate-700 text-sm px-1" data-testid="builder-embedded-title">
                {embeddedTitle || 'Edit Custom Layout'}
              </div>
            ) : (
              <Input
                value={layout.name}
                onChange={(e) => setLayout(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Layout name"
                className="font-semibold border-slate-200"
                data-testid="builder-name"
              />
            )}
          </div>
          <Button onClick={() => addBox('card')} variant="outline" size="sm" data-testid="builder-add-panel">
            <Plus className="w-4 h-4 mr-1" /> Add Panel
          </Button>
          <Button onClick={() => addBox('text')} variant="outline" size="sm" data-testid="builder-add-text">
            <Type className="w-4 h-4 mr-1" /> Add Text
          </Button>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 select-none px-2 border rounded-md h-9" title="Snap to 20px grid">
            <input
              type="checkbox"
              checked={snap}
              onChange={(e) => setSnap(e.target.checked)}
              data-testid="builder-snap-toggle"
            />
            Snap
          </label>
          <Button onClick={save} disabled={saving} data-testid="builder-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {embedded ? embeddedSaveLabel : 'Save Template'}
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 p-4">
        {/* Left — existing layouts (hidden in embedded / inline-edit mode) */}
        {!embedded && (
        <aside className="lg:w-56 bg-white rounded-lg p-3 shadow-sm h-fit">
          <h3 className="text-xs font-bold uppercase text-slate-500 tracking-widest mb-2">Your Templates</h3>
          <button
            onClick={() => navigate('/admin/layout-builder/new')}
            className={`w-full text-left px-2 py-1.5 rounded text-sm mb-1 ${isNew ? 'bg-slate-100 font-semibold' : 'hover:bg-slate-50'}`}
            data-testid="builder-new"
          >
            + New Layout
          </button>
          {existingList.map(l => (
            <div
              key={l.id}
              className={`group flex items-center gap-1 rounded text-sm ${layoutId === l.id ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
            >
              {renamingId === l.id ? (
                <form
                  className="flex-1 flex"
                  onSubmit={(e) => { e.preventDefault(); renameTemplate(l.id); }}
                  onBlur={() => renameTemplate(l.id)}
                >
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border rounded bg-white outline-none"
                    data-testid={`rename-input-${l.id}`}
                  />
                </form>
              ) : (
                <button
                  onClick={() => navigate(`/admin/layout-builder/${l.id}`)}
                  className={`flex-1 text-left px-2 py-1.5 truncate ${layoutId === l.id ? 'font-semibold' : ''}`}
                  data-testid={`builder-list-${l.id}`}
                >
                  {l.name}
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setRenamingId(l.id); setRenameValue(l.name); }}
                className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-opacity"
                title="Rename"
                data-testid={`rename-btn-${l.id}`}
              >
                <Icons.Pencil className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); deleteTemplate(l.id); }}
                className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 text-slate-400 hover:text-red-600 transition-opacity"
                title="Delete"
                data-testid={`delete-btn-${l.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </aside>
        )}

        {/* Middle — canvas */}
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Eyebrow</label>
                <Input value={layout.eyebrow} onChange={(e) => setLayout(p => ({ ...p, eyebrow: e.target.value }))} className="text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Accent color</label>
                <input type="color" value={layout.accent_color} onChange={(e) => setLayout(p => ({ ...p, accent_color: e.target.value }))} className="w-full h-9 rounded border" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Section title</label>
                <Input value={layout.section_title} onChange={(e) => setLayout(p => ({ ...p, section_title: e.target.value }))} className="text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Section subtitle</label>
                <Input value={layout.section_subtitle} onChange={(e) => setLayout(p => ({ ...p, section_subtitle: e.target.value }))} className="text-sm" />
              </div>
            </div>
          </div>

          <div ref={canvasWrapRef} className="w-full">
            <div
              className="relative rounded-lg overflow-hidden mx-auto"
              style={{
                width: CANVAS_W * canvasScale,
                height: CANVAS_H * canvasScale,
                backgroundColor: '#f8fafc',
                backgroundImage: snap
                  ? 'linear-gradient(#bfdbfe 1px, transparent 1px), linear-gradient(90deg, #bfdbfe 1px, transparent 1px)'
                  : 'linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)',
                backgroundSize: `${20 * canvasScale}px ${20 * canvasScale}px`,
                border: '1px solid #cbd5e1',
              }}
              onClick={() => setSelectedId(null)}
              data-testid="builder-canvas"
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0, left: 0,
                  width: CANVAS_W,
                  height: CANVAS_H,
                  transform: `scale(${canvasScale})`,
                  transformOrigin: 'top left',
                }}
              >
                {layout.boxes.map(b => (
                  <CanvasBox
                    key={b.id}
                    box={b}
                    selected={selectedId === b.id}
                    onSelect={(id) => setSelectedId(id)}
                    onChange={updateBox}
                    canvasScale={canvasScale}
                    snap={snap}
                    getSnap={getSnap}
                    onGuidesChange={setActiveGuides}
                  />
                ))}
                {/* Magnetic snap guide lines */}
                {activeGuides.map((g, i) => (
                  <div
                    key={i}
                    data-testid={`snap-guide-${g.type}-${i}`}
                    style={{
                      position: 'absolute',
                      left: g.type === 'v' ? g.pos - 0.5 : 0,
                      top: g.type === 'h' ? g.pos - 0.5 : 0,
                      width: g.type === 'v' ? 1 : CANVAS_W,
                      height: g.type === 'h' ? 1 : CANVAS_H,
                      backgroundColor: '#f43f5e',
                      pointerEvents: 'none',
                      zIndex: 9999,
                    }}
                  />
                ))}
              </div>
              {layout.boxes.length === 0 && (
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-slate-400">
                  <MousePointer2 className="w-10 h-10 mb-2" />
                  <p className="text-sm">Empty canvas. Click <b>Add Panel</b> to start designing.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right — inspector */}
        <aside className="lg:w-80 bg-white rounded-lg shadow-sm h-fit">
          <Inspector
            box={selectedBox}
            onChange={updateBox}
            onDuplicate={duplicateBox}
            onDelete={deleteBox}
            onSendBack={() => bumpZ(-1)}
            onBringForward={() => bumpZ(1)}
          />
        </aside>
      </div>
    </div>
  );
};

export default AdminLayoutBuilder;
